import { useState, useEffect } from 'react';
import { inject } from 'mobx-react';
import { Row, Col, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';

//components
import withLocalization from '../../../hoc/withLocalization';
import Button from '../../../components/CustomButton/CustomButton';
import { SingleUserGroupsWidget } from '../../../components/GenericForm/Widgets/UserGroupWidget';
import TablePagination from '../../../components/GenericList/TablePagination';

//elements
import LoadingSpinner from '../../../elements/LoadingSpinner';

//utils
import { modulePermissionsMap } from '../../../utils/constants';

//styles
import '../Roster/RosterScheduler/css/workPatternStyle.css';

const PM_MODULES = Object.entries(modulePermissionsMap).map(([key, m]) => ({
    key, label: m.label, colorClass: m.colorClass,
    permissions: m.permissions,
}));

const MEMBER_MODULES = Object.entries(modulePermissionsMap)
    .filter(([, m]) => m.memberAccess)
    .map(([key, m]) => ({
        key, label: m.label, colorClass: m.colorClass,
        permissions: m.permissions.filter(p => p.memberAccess !== false),
    }));

const buildColumns = (modules) =>
    modules.flatMap(m =>
        m.permissions.map(p => ({
            moduleKey: m.key,
            colorClass: m.colorClass,
            permKey: p.value,
            permLabel: p.label,
            description: p.description,
            bit: p.bit,
        }))
    );

const PM_COLUMNS = buildColumns(PM_MODULES);
const MEMBER_COLUMNS = buildColumns(MEMBER_MODULES);

const BulkRosterPermissions = ({ userStore, commonStore, t }) => {
    const currentUser = userStore.currentUser;
    const isLoggedInPm = currentUser?.user_type === 'pm';
    const pmMembers = isLoggedInPm && currentUser?.Manages?.length ? currentUser.Manages.join(',') : '';

    const [userType, setUserType] = useState('pm');
    const [nameFilter, setNameFilter] = useState('');
    const [appliedName, setAppliedName] = useState('');
    const [groupFilter, setGroupFilter] = useState('');
    const [users, setUsers] = useState([]);
    const [originalPermissionsMap, setOriginalPermissionsMap] = useState({});
    const [pendingChanges, setPendingChanges] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(0);
    const pageSize = 10;

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async (params = {}) => {
        const { type = userType, name = appliedName, group = groupFilter, pg = page } = params;
        setLoading(true);
        try {
            const response = await userStore.loadUsersWithPermissions({ user_type: type, name, groupCodes: group, page: pg - 1, pagesize: pageSize, pmMembers });
            const loadedUsers = response.rows || [];
            const origMap = {};
            loadedUsers.forEach(user => {
                const perms = user.permissions || {};
                const normalizedPerms = {};
                Object.keys(perms).forEach(moduleKey => {
                    normalizedPerms[moduleKey] = Number(perms[moduleKey]) || 0;
                });
                origMap[user.id] = normalizedPerms;
            });
            setUsers(loadedUsers);
            setPages(response.pages || 0);
            setOriginalPermissionsMap(origMap);
            setPendingChanges({});
        } catch (err) {
            commonStore.addNotification(t('Error loading'), null, 'error');
        } finally {
            setLoading(false);
        }
    };

    const getPendingValue = (userId, moduleKey) => {
        if (pendingChanges[userId] && pendingChanges[userId][moduleKey] !== undefined)
            return pendingChanges[userId][moduleKey];
        return undefined;
    };

    const getBitmask = (userId, moduleKey) => {
        const pending = getPendingValue(userId, moduleKey);
        if (pending !== undefined) return pending;
        return (originalPermissionsMap[userId] && originalPermissionsMap[userId][moduleKey]) || 0;
    };

    const isBitSet = (userId, moduleKey, bit) => (getBitmask(userId, moduleKey) & bit) !== 0;

    const isPending = (userId, moduleKey, bit) => {
        const pending = getPendingValue(userId, moduleKey);
        if (pending === undefined) return false;
        const orig = (originalPermissionsMap[userId] && originalPermissionsMap[userId][moduleKey]) || 0;
        return ((orig & bit) !== 0) !== ((pending & bit) !== 0);
    };

    const computeCascade = (userId, moduleKey, newBitmask) => {
        const oldBitmask = getBitmask(userId, moduleKey);
        const result = { [moduleKey]: newBitmask };

        const moduleConfig = modulePermissionsMap[moduleKey];
        if (moduleConfig) {
            const viewPerm = moduleConfig.permissions.find(p => p.value === 'view');
            const viewBit = viewPerm ? viewPerm.bit : 0;
            if (viewBit) {
                if ((oldBitmask & viewBit) && !(newBitmask & viewBit)) {
                    moduleConfig.permissions.forEach(p => {
                        if (p.requires && p.requires.includes('view')) result[moduleKey] &= ~p.bit;
                    });
                } else {
                    moduleConfig.permissions.forEach(p => {
                        if (p.requires && p.requires.includes('view') && !(oldBitmask & p.bit) && (newBitmask & p.bit))
                            result[moduleKey] |= viewBit;
                    });
                }
            }
        }

        const ROSTER_SUB_MODULES = ['md_QuickPlanner', 'md_WorkPattern'];
        if (moduleKey === 'md_Roster') {
            const viewWasOn = !!(oldBitmask & 1);
            const viewIsNowOn = !!(result['md_Roster'] & 1);
            if (viewWasOn && !viewIsNowOn) {
                ROSTER_SUB_MODULES.forEach(sub => { result[sub] = 0; });
            } else if (!viewWasOn && viewIsNowOn) {
                const origPerms = originalPermissionsMap[userId] || {};
                ROSTER_SUB_MODULES.forEach(sub => { result[sub] = origPerms[sub] || 0; });
            }
        } else if (ROSTER_SUB_MODULES.includes(moduleKey)) {
            if (result[moduleKey] & ~oldBitmask) {
                const rosterBitmask = getBitmask(userId, 'md_Roster');
                if (!(rosterBitmask & 1)) result['md_Roster'] = rosterBitmask | 1;
            }
        }

        return result;
    };

    const toggleCell = (userId, moduleKey, bit) => {
        const newBitmask = getBitmask(userId, moduleKey) ^ bit;
        const updates = computeCascade(userId, moduleKey, newBitmask);
        setPendingChanges(prev => ({
            ...prev,
            [userId]: { ...(prev[userId] || {}), ...updates },
        }));
    };

    const getColumnState = (moduleKey, bit) => {
        if (!users.length) return 'none';
        const enabledCount = users.filter(u => isBitSet(u.id, moduleKey, bit)).length;
        if (enabledCount === 0) return 'none';
        if (enabledCount === users.length) return 'all';
        return 'mixed';
    };

    const toggleColumn = (moduleKey, bit) => {
        const enable = getColumnState(moduleKey, bit) !== 'all';
        const changes = { ...pendingChanges };
        users.forEach(user => {
            const current = getBitmask(user.id, moduleKey);
            const newBitmask = enable ? (current | bit) : (current & ~bit);
            const updates = computeCascade(user.id, moduleKey, newBitmask);
            changes[user.id] = { ...(changes[user.id] || {}), ...updates };
        });
        setPendingChanges(changes);
    };

    const getChangeCount = () => {
        let count = 0;
        Object.entries(pendingChanges).forEach(([userId, modules]) => {
            Object.entries(modules).forEach(([moduleKey, newBitmask]) => {
                const orig = (originalPermissionsMap[userId] && originalPermissionsMap[userId][moduleKey]) || 0;
                if (newBitmask !== orig) count++;
            });
        });
        return count;
    };

    const confirmDiscardChanges = (proceed) => {
        if (getChangeCount() === 0) { proceed(); return; }
        confirmAlert({
            title: t('Confirm discard'),
            message: t('You have unsaved permission changes that will be lost. Continue?'),
            buttons: [
                { label: t('Cancel'), onClick: () => { } },
                { label: t('Discard Changes'), onClick: proceed },
            ],
        });
    };

    const handleSave = () => {
        const changes = [];
        Object.entries(pendingChanges).forEach(([userId, modules]) => {
            Object.entries(modules).forEach(([moduleKey, newBitmask]) => {
                const orig = (originalPermissionsMap[userId] && originalPermissionsMap[userId][moduleKey]) || 0;
                if (newBitmask !== orig)
                    changes.push({ user_id: parseInt(userId), module_name: moduleKey, permission_code: newBitmask });
            });
        });
        if (!changes.length) return;
        const usersAffected = new Set(changes.map(c => c.user_id)).size;
        confirmAlert({
            title: t('Confirm Update'),
            message: `${t('Updating permissions for')} ${usersAffected} ${t('user(s)')}. ${t('Continue')}?`,
            buttons: [
                {
                    label: t('Yes'),
                    onClick: async () => {
                        setSaving(true);
                        try {
                            await userStore.bulkSaveRosterPermissions({
                                permissionUpdates: changes,
                                pmMembers: isLoggedInPm ? (currentUser.Manages || []) : undefined,
                            });
                            commonStore.addNotification(t('Saved'), null, 'success');
                            await loadUsers();
                        } catch (err) {
                            commonStore.addNotification(err.message || t('Error saving'), null, 'error');
                        } finally {
                            setSaving(false);
                        }
                    },
                },
                { label: t('No'), onClick: () => { } },
            ],
        });
    };

    const handleUserTypeChange = (type) => {
        if (type === userType) return;
        confirmDiscardChanges(() => {
            setUserType(type);
            setPendingChanges({});
            setPage(1);
            loadUsers({ type, pg: 1 });
        });
    };

    const applyFilters = () => {
        confirmDiscardChanges(() => {
            setAppliedName(nameFilter);
            setPendingChanges({});
            setPage(1);
            loadUsers({ name: nameFilter, pg: 1 });
        });
    };

    const handleGroupFilter = (value) => {
        confirmDiscardChanges(() => {
            const group = value || '';
            setGroupFilter(group);
            setPendingChanges({});
            setPage(1);
            loadUsers({ group, pg: 1 });
        });
    };

    const handlePagination = (newPage) => {
        confirmDiscardChanges(() => {
            setPage(newPage);
            setPendingChanges({});
            loadUsers({ pg: newPage });
        });
    };

    const renderColumnHeaderCheckbox = (col) => {
        const state = getColumnState(col.moduleKey, col.bit);
        return (
            <div className="d-flex flex-column align-items-center gap-1">
                <input
                    type="checkbox"
                    className="form-check-input mt-0 perm-col-checkbox cursor-pointer"
                    checked={state === 'all'}
                    onChange={() => toggleColumn(col.moduleKey, col.bit)}
                />
                <div className="d-flex align-items-center gap-1">
                    <span className="font-size-12 fw-medium">{t(col.permLabel)}</span>
                    {col.description && (
                        <OverlayTrigger placement="top" overlay={<Tooltip id={`tip-${col.moduleKey}-${col.permKey}`}>{t(col.description)}</Tooltip>}>
                            <i className="fa fa-info-circle text-muted opacity-50 font-size-12" aria-hidden="true" />
                        </OverlayTrigger>
                    )}
                </div>
            </div>
        );
    };

    const renderCell = (user, col) => {
        const isSet = isBitSet(user.id, col.moduleKey, col.bit);
        const changed = isPending(user.id, col.moduleKey, col.bit);
        return (
            <td
                key={`${user.id}-${col.moduleKey}-${col.permKey}`}
                className={`perm-cell cursor-pointer text-center align-middle text-nowrap${changed ? ' perm-cell-changed' : ''}`}
                onClick={() => toggleCell(user.id, col.moduleKey, col.bit)}
            >
                {isSet ? <i className="fa fa-check-circle fa-lg text-success" /> : <i className="fa fa-times-circle fa-lg text-secondary" />}
            </td>
        );
    };

    const currentModules = userType === 'pm' ? PM_MODULES : MEMBER_MODULES;
    const currentColumns = userType === 'pm' ? PM_COLUMNS : MEMBER_COLUMNS;
    const changeCount = getChangeCount();

    return (
        <>
            <Row className="my-2 align-items-center gx-2">
                <Col xs="auto">
                    <div className="d-flex">
                        <button
                            className={`tab-btn tab-btn-left${userType === 'pm' ? ' active' : ''}`}
                            onClick={() => handleUserTypeChange('pm')}
                        >
                            {t('PM')}
                        </button>
                        <button
                            className={`tab-btn tab-btn-right${userType === 'member' ? ' active' : ''}`}
                            onClick={() => handleUserTypeChange('member')}
                        >
                            {t('Member')}
                        </button>
                    </div>
                </Col>
                <Col xs={3}>
                    <div className="conversation-search">
                        <input
                            type="text"
                            className="conversation-search-input"
                            placeholder={t('Search by employee name')}
                            value={nameFilter}
                            onChange={e => setNameFilter(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && applyFilters()}
                        />
                        <i className="fa fa-search conversation-search-icon" onClick={applyFilters} />
                    </div>
                </Col>
                <Col xs={3}>
                    <SingleUserGroupsWidget value={groupFilter} onChange={handleGroupFilter} />
                </Col>
                <Col xs="auto" className="ms-auto">
                    {changeCount > 0 && (
                        <Button fill onClick={handleSave} disabled={saving}>
                            {saving ? t('Saving...') : `${t('Save Changes')} (${changeCount})`}
                        </Button>
                    )}
                </Col>
            </Row>

            {loading ? <LoadingSpinner /> : (
                <>
                    <p className="font-size-11 text-muted mb-1">
                        <i className="fa fa-info-circle me-1" aria-hidden="true"></i>
                        {t('Note')} : {t('Some roster features require Roster View. It will be enabled automatically when needed.')}
                    </p>
                    <div className="bulk-perm-table-wrapper">
                        <Table bordered className="bulk-perm-table mb-0">
                            <thead>
                                <tr>
                                    <th className="bulk-perm-name-col text-start fw-semibold bg-white align-middle text-nowrap" rowSpan={2}>
                                        {t('Employee')}
                                    </th>
                                    {currentModules.map(m => (
                                        <th
                                            key={m.key}
                                            colSpan={m.permissions.length}
                                            className={`module-header font-size-12 fw-bold text-uppercase text-center align-middle text-nowrap ${m.colorClass}`}
                                        >
                                            {t(m.label)}
                                        </th>
                                    ))}
                                </tr>
                                <tr>
                                    {currentColumns.map(col => (
                                        <th
                                            key={`${col.moduleKey}-${col.permKey}`}
                                            className="perm-col-header text-center align-middle text-nowrap"
                                        >
                                            {renderColumnHeaderCheckbox(col)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {users.length === 0 ? (
                                    <tr>
                                        <td colSpan={currentColumns.length + 1} className="text-center align-middle text-nowrap text-muted py-4">
                                            {t('No employees found')}
                                        </td>
                                    </tr>
                                ) : users.map(user => (
                                    <tr key={user.id}>
                                        <td className="bulk-perm-name-col text-start fw-semibold bg-white align-middle text-nowrap">
                                            {user.first_name} {user.last_name}
                                        </td>
                                        {currentColumns.map(col => renderCell(user, col))}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    {pages > 1 && (
                        <div className="my-1">
                            <TablePagination
                                pages={pages}
                                page={page - 1}
                                handlePagination={handlePagination}
                                requestData={() => { }}
                                filters={{}}
                            />
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default inject('userStore', 'commonStore')(withLocalization(BulkRosterPermissions));
